import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable, { applyPlugin } from "jspdf-autotable";
import { useCompany } from "../../context/CompanyContext";
import { showToast } from "../../utils/customToast";
import {
  FaFileExcel,
  FaFilePdf,
  FaPrint,
  FaSearch,
  FaCalendarAlt,
  FaChevronLeft,
  FaChevronRight,
  FaRedoAlt,
} from "react-icons/fa";
import "./DayBook.css";
import { MasterTableSkeleton } from "../../Component/Skeleton";
import SearchableSelect from "../../Component/SearchableSelect/SearchableSelect";

try {
  applyPlugin(jsPDF);
} catch (e) {
  // autoTable already applied or fallback
}

const formatCellAmount = (val) => {
  const num = Number(val || 0);
  if (!num || num === 0) return "";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatTotalAmount = (val) => {
  const num = Number(val || 0);
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDisplayDate = (dStr) => {
  if (!dStr) return "-";
  const d = new Date(dStr);
  if (isNaN(d.getTime())) return dStr;
  const day = String(d.getDate()).padStart(2, "0");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const m = months[d.getMonth()];
  const y = d.getFullYear();
  return `${day}-${m}-${y}`;
};

export default function DayBook() {
  const navigate = useNavigate();
  const { selectedCompany } = useCompany();
  const BASE_API_URL =
    process.env.REACT_APP_BASE_API_URL ||
    process.env.REACT_APP_API_URL ||
    "http://localhost:5000/api";

  const companyId = useMemo(() => {
    return (
      selectedCompany?.CompID ||
      selectedCompany?.id ||
      sessionStorage.getItem("CompID") ||
      sessionStorage.getItem("companyId") ||
      "1147"
    );
  }, [selectedCompany]);

  const companyInfo = useMemo(() => {
    return {
      name:
        selectedCompany?.Name ||
        selectedCompany?.name ||
        selectedCompany?.CompanyName ||
        "DAZY TOURE TRAVEL'S",
      address:
        selectedCompany?.Address ||
        selectedCompany?.address ||
        "Opp. GPO, M.I. Road, Jaipur-302001",
      gstin: selectedCompany?.GSTNO || selectedCompany?.gstin || "",
      stateCode: selectedCompany?.StateCode || selectedCompany?.stateCode || "08",
    };
  }, [selectedCompany]);

  // Default dates: 1st of current month to today
  const defaultDates = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return {
      from: `${y}-${m}-01`,
      to: `${y}-${m}-${d}`,
    };
  }, []);

  const [fromDate, setFromDate] = useState(defaultDates.from);
  const [toDate, setToDate] = useState(defaultDates.to);
  const [selectedVType, setSelectedVType] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState("detail"); // "short" or "detail"
  const [showNarration, setShowNarration] = useState(false);
  const [narrationFilter, setNarrationFilter] = useState("");

  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({ totalRecords: 0, totalDebit: 0, totalCredit: 0 });
  const [loading, setLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Live timestamp for "As on" badge
  const [liveTimestamp, setLiveTimestamp] = useState("");

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const d = String(now.getDate()).padStart(2, "0");
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const m = months[now.getMonth()];
      const y = now.getFullYear();
      let hours = now.getHours();
      const mins = String(now.getMinutes()).padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;
      setLiveTimestamp(`${d}-${m}-${y} ${hours}:${mins} ${ampm}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch Day Book data
  const fetchDayBook = useCallback(
    async (overrideParams = {}) => {
      if (!companyId) return;
      setLoading(true);
      try {
        const params = {
          fromDate: overrideParams.fromDate !== undefined ? overrideParams.fromDate : fromDate,
          toDate: overrideParams.toDate !== undefined ? overrideParams.toDate : toDate,
          vtype: overrideParams.vtype !== undefined ? overrideParams.vtype : selectedVType,
        };

        const res = await axios.get(`${BASE_API_URL}/reports/day-book/${companyId}`, { params });
        if (res.data?.success) {
          const list = res.data.data || [];
          setRecords(list);
          setSummary(
            res.data.summary || {
              totalRecords: list.length,
              totalDebit: list.reduce((acc, r) => acc + (Number(r.debit) || 0), 0),
              totalCredit: list.reduce((acc, r) => acc + (Number(r.credit) || 0), 0),
            }
          );
        } else {
          setRecords([]);
          setSummary({ totalRecords: 0, totalDebit: 0, totalCredit: 0 });
        }
      } catch (err) {
        console.error("Error loading day book:", err);
        showToast.error("Failed to load Day Book entries");
      } finally {
        setLoading(false);
      }
    },
    [BASE_API_URL, companyId, fromDate, toDate, selectedVType]
  );

  useEffect(() => {
    fetchDayBook();
  }, [fetchDayBook]);

  // Filtered records by client search & narration (100% client-side without API calls)
  const filteredRecords = useMemo(() => {
    let result = records;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (r) =>
          r.voucherNo?.toLowerCase().includes(term) ||
          r.particulars?.toLowerCase().includes(term) ||
          r.firstDebit?.toLowerCase().includes(term) ||
          r.firstCredit?.toLowerCase().includes(term) ||
          r.type?.toLowerCase().includes(term) ||
          r.date?.toLowerCase().includes(term) ||
          (r.narration && r.narration.toLowerCase().includes(term))
      );
    }
    if (showNarration && narrationFilter.trim()) {
      const nTerm = narrationFilter.toLowerCase();
      result = result.filter(
        (r) => r.narration && r.narration.toLowerCase().includes(nTerm)
      );
    }
    return result;
  }, [records, searchTerm, showNarration, narrationFilter]);

  // Reset page when dataset changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, rowsPerPage, selectedVType, narrationFilter, showNarration]);

  // Paginated records
  const paginatedRecords = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredRecords.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRecords, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredRecords.length / rowsPerPage) || 1;

  // Totals for filtered records
  const displaySummary = useMemo(() => {
    if (!searchTerm.trim() && (!showNarration || !narrationFilter.trim())) return summary;
    const dr = filteredRecords.reduce((acc, r) => acc + (Number(r.debit) || 0), 0);
    const cr = filteredRecords.reduce((acc, r) => acc + (Number(r.credit) || 0), 0);
    return {
      totalRecords: filteredRecords.length,
      totalDebit: Math.round(dr * 100) / 100,
      totalCredit: Math.round(cr * 100) / 100,
    };
  }, [filteredRecords, summary, searchTerm, showNarration, narrationFilter]);

  // Handle View click
  const handleView = () => {
    fetchDayBook();
  };

  // Handle Reset click
  const handleReset = () => {
    setFromDate(defaultDates.from);
    setToDate(defaultDates.to);
    setSelectedVType("all");
    setSearchTerm("");
    setNarrationFilter("");
    setShowNarration(false);
    setViewMode("detail");
    fetchDayBook({
      fromDate: defaultDates.from,
      toDate: defaultDates.to,
      vtype: "all",
    });
  };

  // Export to Excel - Exactly matching Monthly Summary & Ledger Transactions formal structure
  const handleExportExcel = () => {
    try {
      if (filteredRecords.length === 0) {
        showToast.info("No records available to export");
        return;
      }

      const activeCompName = String(companyInfo.name).toUpperCase();
      const compDetails = `Address: ${companyInfo.address || ""}  |  GSTIN: ${companyInfo.gstin || ""}  |  State: ${companyInfo.stateCode || "08"}`;
      const reportTitle = "DAY BOOK REPORT";
      const vTypeLabel =
        selectedVType === "all"
          ? "All Vouchers"
          : selectedVType === "1"
          ? "Receipt"
          : selectedVType === "2"
          ? "Payment"
          : selectedVType === "3"
          ? "Journal"
          : selectedVType === "4"
          ? "Contra"
          : selectedVType === "5"
          ? "Sales"
          : selectedVType === "6"
          ? "Purchase"
          : selectedVType;
      const periodSubtitle = `Period: ${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}  |  Voucher Type: ${vTypeLabel}  |  As on: ${liveTimestamp}`;

      const headers = ["Date", "Voucher No.", "Type", "Particulars", "Debit (INR)", "Credit (INR)"];

      const isShort = viewMode === "short";
      const dataRows = [];
      filteredRecords.forEach((r) => {
        const debitLines = (r.lines || []).filter((l) => l.type === "Dr");
        const creditLines = (r.lines || []).filter((l) => l.type === "Cr");

        if (debitLines.length === 0 && creditLines.length === 0) {
          const firstDebit = r.firstDebit || r.particulars || "General Entry";
          const firstCredit = r.firstCredit ? `  ${r.firstCredit}` : `  ${r.particulars || "General Entry"}`;
          dataRows.push([
            r.date,
            r.voucherNo,
            r.type,
            firstDebit,
            Number(r.debit) > 0 ? Number(r.debit) : "",
            "",
          ]);
          if (!isShort && Number(r.credit) > 0) {
            dataRows.push([
              "",
              "",
              "",
              firstCredit,
              "",
              Number(r.credit),
            ]);
          }
        } else {
          if (debitLines.length === 0) {
            dataRows.push([
              r.date,
              r.voucherNo,
              r.type,
              r.firstDebit || r.particulars || "General Entry",
              Number(r.debit) > 0 ? Number(r.debit) : "",
              "",
            ]);
          } else {
            debitLines.forEach((dr, idx) => {
              const drAmt = Number(dr.debit) || Number(r.debit) || 0;
              dataRows.push([
                idx === 0 ? r.date : "",
                idx === 0 ? r.voucherNo : "",
                idx === 0 ? r.type : "",
                dr.ledgerName,
                drAmt > 0 ? drAmt : "",
                "",
              ]);
            });
          }

          if (!isShort) {
            creditLines.forEach((cr, idx) => {
              const crAmt = Number(cr.credit) || Number(r.credit) || 0;
              dataRows.push([
                debitLines.length === 0 && idx === 0 ? r.date : "",
                debitLines.length === 0 && idx === 0 ? r.voucherNo : "",
                debitLines.length === 0 && idx === 0 ? r.type : "",
                `  ${cr.ledgerName}`,
                "",
                crAmt > 0 ? crAmt : "",
              ]);
            });
          }
        }

        if (showNarration && r.narration) {
          dataRows.push([
            "",
            "",
            "",
            `  (Narration: ${r.narration})`,
            "",
            "",
          ]);
        }
      });

      const totalRow = [
        "",
        "",
        "",
        "Total",
        Number(displaySummary.totalDebit) || 0,
        Number(displaySummary.totalCredit) || 0,
      ];

      const wsData = [
        [activeCompName],
        [compDetails],
        [reportTitle],
        [periodSubtitle],
        [],
        headers,
        ...dataRows,
        totalRow,
        [],
        ["etour - Design & Develop by Response Infoway"],
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(wsData);
      const lastCol = headers.length - 1;
      worksheet["!merges"] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: lastCol } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: lastCol } },
        { s: { r: 2, c: 0 }, e: { r: 2, c: lastCol } },
        { s: { r: 3, c: 0 }, e: { r: 3, c: lastCol } },
      ];

      worksheet["!cols"] = [
        { wch: 14 }, // Date
        { wch: 14 }, // Voucher No.
        { wch: 14 }, // Type
        { wch: 50 }, // Particulars
        { wch: 18 }, // Debit
        { wch: 18 }, // Credit
      ];

      // Format number cells in worksheet with standard Indian 2-decimal format
      const range = XLSX.utils.decode_range(worksheet["!ref"]);
      for (let R = 5; R <= range.e.r; ++R) {
        const cellDr = worksheet[XLSX.utils.encode_cell({ r: R, c: 4 })];
        if (cellDr && typeof cellDr.v === "number") {
          cellDr.z = "#,##0.00";
        }
        const cellCr = worksheet[XLSX.utils.encode_cell({ r: R, c: 5 })];
        if (cellCr && typeof cellCr.v === "number") {
          cellCr.z = "#,##0.00";
        }
      }


      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Day_Book");
      XLSX.writeFile(workbook, `Day_Book_${fromDate}_to_${toDate}.xlsx`);
      showToast.success("Excel exported successfully");
    } catch (err) {
      console.error("Failed to export Excel:", err);
      showToast.error("Failed to export Excel");
    }
  };

  // Export to PDF - Exactly matching Monthly Summary & Ledger Transactions formal structure
  const handleExportPDF = () => {
    try {
      if (filteredRecords.length === 0) {
        showToast.info("No records available to export");
        return;
      }

      const doc = new jsPDF("p", "mm", "a4");
      const pageWidth = doc.internal.pageSize.getWidth();
      const activeCompName = String(companyInfo.name).toUpperCase();
      const compLine2 = [
        companyInfo.address,
        companyInfo.gstin ? `GSTIN: ${companyInfo.gstin}` : "",
        companyInfo.stateCode ? `State Code: ${companyInfo.stateCode}` : "",
      ]
        .filter(Boolean)
        .join("   |   ");
      const reportTitle = "DAY BOOK";

      // 1. Company Name Header (Centered, Bold, 15pt)
      doc.setFont("helvetica", "bold");
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
      doc.text(activeCompName, pageWidth / 2, 14, { align: "center" });

      // 2. Company Details
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);
      doc.text(compLine2, pageWidth / 2, 19, { align: "center" });

      // 3. Report Title Underlined
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10.5);
      doc.setTextColor(0, 0, 0);
      doc.text(reportTitle, pageWidth / 2, 26, { align: "center" });
      const titleWidth = doc.getTextWidth(reportTitle);
      doc.setLineWidth(0.75);
      doc.setDrawColor(0, 0, 0);
      doc.line(pageWidth / 2 - titleWidth / 2, 27.5, pageWidth / 2 + titleWidth / 2, 27.5);

      // 4. Solid formal divider line
      doc.setLineWidth(1);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 31, pageWidth - 14, 31);

      // 5. Meta Information Bar
      const vTypeLabel =
        selectedVType === "all"
          ? "All Vouchers"
          : selectedVType === "1"
          ? "Receipt"
          : selectedVType === "2"
          ? "Payment"
          : selectedVType === "3"
          ? "Journal"
          : selectedVType === "4"
          ? "Contra"
          : selectedVType === "5"
          ? "Sales"
          : selectedVType === "6"
          ? "Purchase"
          : selectedVType;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(8.5);
      doc.setTextColor(0, 0, 0);
      doc.text(`Voucher Type: ${vTypeLabel}`, 14, 36);
      doc.text(
        `Period: ${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}   |   Date: ${liveTimestamp}`,
        pageWidth - 14,
        36,
        { align: "right" }
      );

      // 6. Solid formal divider line below Meta bar
      doc.setLineWidth(0.5);
      doc.setDrawColor(0, 0, 0);
      doc.line(14, 39, pageWidth - 14, 39);

      const isShort = viewMode === "short";
      const tableRows = filteredRecords.map((r) => {
        const firstDebit = r.firstDebit || (r.lines ? r.lines.find((l) => l.type === "Dr")?.ledgerName : null);
        const firstCredit = r.firstCredit || (r.lines ? r.lines.find((l) => l.type === "Cr")?.ledgerName : null);
        const parts = [];
        if (firstDebit) parts.push(firstDebit);
        if (!isShort && firstCredit) parts.push(`  ${firstCredit}`);
        if (!firstDebit && !firstCredit) parts.push(r.particulars);
        if (showNarration && r.narration) {
          parts.push(`  (Narration: ${r.narration})`);
        }

        const debitStr = firstDebit && r.debit > 0 ? formatCellAmount(r.debit) : "";
        const creditStr = !isShort && firstCredit && r.credit > 0 ? formatCellAmount(r.credit) : "";

        const drCol = (!isShort && firstCredit) ? (debitStr ? `${debitStr}\n` : "") : debitStr;
        const crCol = firstDebit ? (creditStr ? `\n${creditStr}` : "") : creditStr;

        return [
          r.date,
          r.voucherNo,
          r.type,
          parts.join("\n"),
          drCol,
          crCol,
        ];
      });

      tableRows.push([
        "",
        "",
        "",
        "Total",
        formatTotalAmount(displaySummary.totalDebit),
        formatTotalAmount(displaySummary.totalCredit),
      ]);

      autoTable(doc, {
        head: [
          [
            { content: "Date", styles: { halign: "left" } },
            { content: "Voucher No.", styles: { halign: "left" } },
            { content: "Type", styles: { halign: "left" } },
            { content: "Particulars", styles: { halign: "left" } },
            { content: "Debit (INR)", styles: { halign: "right" } },
            { content: "Credit (INR)", styles: { halign: "right" } },
          ],
        ],
        body: tableRows,
        startY: 42,
        theme: "striped",
        headStyles: {
          fillColor: [6, 78, 72], // --primary-teal
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [30, 41, 59],
          cellPadding: 2.5,
        },
        columnStyles: {
          0: { cellWidth: 24, halign: "left" },
          1: { cellWidth: 22, halign: "left" },
          2: { cellWidth: 20, halign: "left" },
          3: { cellWidth: "auto", halign: "left" },
          4: { cellWidth: 28, halign: "right" },
          5: { cellWidth: 28, halign: "right" },
        },
        didParseCell: (data) => {
          // Explicitly align Debit & Credit columns to the right in headers, body, and totals
          if (data.column.index === 4 || data.column.index === 5) {
            data.cell.styles.halign = "right";
          }
          if (data.row.index === tableRows.length - 1) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [240, 245, 245];
            data.cell.styles.textColor = [6, 78, 72];
            if (data.column.index === 3) {
              data.cell.styles.halign = "left";
            }
          }
        },
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const curPage = data.pageNumber;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.setTextColor(120, 120, 120);
          doc.text(
            "etour - Design & Develop by Response Infoway",
            14,
            doc.internal.pageSize.getHeight() - 8
          );
          doc.text(
            `Page ${curPage} of ${pageCount}`,
            pageWidth - 14,
            doc.internal.pageSize.getHeight() - 8,
            { align: "right" }
          );
        },
        margin: { left: 14, right: 14, bottom: 15 },
      });

      doc.save(`Day_Book_${fromDate}_to_${toDate}.pdf`);
      showToast.success("PDF exported successfully");
    } catch (err) {
      console.error("Failed to export PDF:", err);
      showToast.error("Failed to export PDF");
    }
  };

  // Print
  const handlePrint = () => {
    if (filteredRecords.length === 0) {
      showToast.info("No records available to print");
      return;
    }
    window.print();
  };

  return (
    <div className="db_page_wrapper">
      {/* 1. Header & Breadcrumb (Matches GST / Tax Register) */}
      <div className="db_breadcrumb_bar d-flex justify-content-between align-items-center">
        <div>
          <span>Reports &gt;</span> Day Book
        </div>
        <div className="db_as_on_badge" title="Live System Timestamp">
          <FaCalendarAlt />
          <span>As on: {liveTimestamp}</span>
        </div>
      </div>

      {/* 2. Filter Bar Card (Matches GST / Tax Register) */}
      <div className="db_filter_bar">
        <div className="d-flex align-items-center gap-1">
          <span className="filter-label">From:</span>
          <input
            type="date"
            className="filter-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ width: 125 }}
          />
          <span style={{ color: "#94a3b8", fontSize: "11px" }}>–</span>
          <span className="filter-label">To:</span>
          <input
            type="date"
            className="filter-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ width: 125 }}
          />
        </div>

        <div className="d-flex align-items-center gap-1">
          <span className="filter-label">Voucher Type:</span>
          <SearchableSelect
            options={[
              { value: "all", label: "All Vouchers" },
              { value: "2", label: "Payment" },
              { value: "1", label: "Receipt" },
              { value: "3", label: "Journal" },
              { value: "4", label: "Contra" },
              { value: "5", label: "Sales" },
              { value: "6", label: "Purchase" },
              { value: "9", label: "Debit Note" },
              { value: "10", label: "Credit Note" },
            ]}
            value={selectedVType}
            onChange={(val) => setSelectedVType(val || "all")}
            placeholder="Voucher Type"
            height="28px"
            width="150px"
            isClearable={false}
          />
        </div>

        {/* Format Toggle (Short / Detail) */}
        <div className="d-flex align-items-center gap-1">
          <span className="filter-label">Format:</span>
          <div className="db_format_toggle">
            <button
              type="button"
              className={`db_format_btn ${viewMode === "short" ? "active" : ""}`}
              onClick={() => setViewMode("short")}
            >
              Short
            </button>
            <button
              type="button"
              className={`db_format_btn ${viewMode === "detail" ? "active" : ""}`}
              onClick={() => setViewMode("detail")}
            >
              Detail
            </button>
          </div>
        </div>

        {/* Narration Filter Checkbox + Search */}
        <div className="d-flex align-items-center gap-1 db_narration_box">
          <label className="db_checkbox_label d-flex align-items-center gap-1 mb-0" style={{ cursor: "pointer", fontSize: "11px", fontWeight: 600, color: "#4a5568" }}>
            <input
              type="checkbox"
              className="db_checkbox"
              checked={showNarration}
              onChange={(e) => setShowNarration(e.target.checked)}
            />
            Narration
          </label>
          {showNarration && (
            <input
              type="text"
              className="filter-input"
              style={{ width: "120px", height: "26px", fontSize: "11px" }}
              placeholder="Search narration..."
              value={narrationFilter}
              onChange={(e) => setNarrationFilter(e.target.value)}
            />
          )}
        </div>

        {/* Quick Search */}
        <div className="d-flex align-items-center gap-1 flex-grow-1" style={{ minWidth: 180 }}>
          <span className="filter-label">Search:</span>
          <div className="position-relative w-100">
            <input
              type="text"
              className="filter-input w-100"
              placeholder="Search voucher, party, type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingRight: 22 }}
            />
            <FaSearch
              className="position-absolute"
              style={{
                right: 7,
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: 10,
                color: "#94a3b8",
                pointerEvents: "none",
              }}
            />
          </div>
        </div>

        <div className="d-flex align-items-center gap-1 ms-auto">
          <button
            type="button"
            className="filter-btn-apply"
            onClick={handleView}
            disabled={loading}
            title="Filter records"
          >
            View
          </button>
          <button
            type="button"
            className="filter-btn-reset"
            onClick={handleReset}
            disabled={loading}
            title="Reset filters"
          >
            Reset
          </button>
          <button
            type="button"
            className="filter-btn-reset"
            onClick={() => fetchDayBook()}
            disabled={loading}
            title="Refresh from database"
            style={{ padding: "0 8px" }}
          >
            <FaRedoAlt className={loading ? "fa-spin" : ""} style={{ fontSize: "10px" }} />
          </button>
        </div>
      </div>

      {/* 3. Table Card Section (Matches GST / Tax Register) */}
      <div className="db_table_card">
        <div className="db_table_header d-flex justify-content-between align-items-center">
          <div className="db_record_count">
            Total Vouchers: <strong>{displaySummary.totalRecords}</strong>
          </div>
          <div className="d-flex align-items-center gap-1 flex-wrap">
            <button
              type="button"
              className="gst_export_btn btn_excel"
              onClick={handleExportExcel}
              title="Export Excel (.xlsx)"
            >
              <FaFileExcel style={{ color: "#16a34a" }} />
              <span>Export Excel</span>
            </button>
            <button
              type="button"
              className="gst_export_btn btn_pdf"
              onClick={handleExportPDF}
              title="Export PDF"
            >
              <FaFilePdf style={{ color: "#dc2626" }} />
              <span>Export PDF</span>
            </button>
            <button
              type="button"
              className="gst_export_btn btn_print"
              onClick={handlePrint}
              title="Print Day Book"
            >
              <FaPrint style={{ color: "#475569" }} />
              <span>Print</span>
            </button>
          </div>
        </div>

        {/* Main Data Table */}
        <div className="table-responsive">
          {loading ? (
            <MasterTableSkeleton columns={6} rows={6} hasToolbar={false} />
          ) : (
            <table className="gst_custom_table db_table">
              <thead>
                <tr>
                  <th style={{ width: "12%" }}>Date</th>
                  <th style={{ width: "13%" }}>Voucher No.</th>
                  <th style={{ width: "13%" }}>Type</th>
                  <th style={{ width: "38%" }}>Particulars</th>
                  <th style={{ width: "12%" }} className="text-right">
                    Debit (₹)
                  </th>
                  <th style={{ width: "12%" }} className="text-right">
                    Credit (₹)
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedRecords.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted" style={{ fontSize: "11px" }}>
                      No vouchers found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  paginatedRecords.map((row) => {
                    const isShort = viewMode === "short";
                    const firstDebit = row.firstDebit || (row.lines ? row.lines.find((l) => l.type === "Dr")?.ledgerName : null);
                    const firstCredit = row.firstCredit || (row.lines ? row.lines.find((l) => l.type === "Cr")?.ledgerName : null);
                    const hasDebit = !!firstDebit;
                    const hasCredit = !isShort && !!firstCredit;

                    return (
                      <tr
                        key={row.voucherId}
                        onClick={() =>
                          navigate(
                            `/transactions/modify-voucher?voucherId=${row.voucherId}`
                          )
                        }
                        style={{ cursor: "pointer" }}
                      >
                        <td className="col_date">{row.date}</td>
                        <td className="col_voucher font-semibold">
                          <span
                            className="db_voucher_click_link"
                            title="Click to view & modify this voucher in Transactions"
                            style={{
                              cursor: "pointer",
                              color: "#0369a1",
                              textDecoration: "none",
                              fontWeight: 700,
                            }}
                          >
                            {row.voucherNo}
                          </span>
                        </td>
                        <td className="col_type">{row.type}</td>
                        <td className="col_party">
                          <div className="db_particulars_cell">
                            {hasDebit && (
                              <div className="db_debit_ledger">{firstDebit}</div>
                            )}
                            {hasCredit && (
                              <div className="db_credit_ledger">{firstCredit}</div>
                            )}
                            {!hasDebit && !hasCredit && (
                              <div className="db_debit_ledger">{row.particulars}</div>
                            )}
                            {showNarration && row.narration && (
                              <div
                                className="db_narration_text"
                                style={{
                                  fontSize: "10.5px",
                                  color: "#64748b",
                                  fontStyle: "italic",
                                  marginTop: "2px",
                                }}
                              >
                                Narration: {row.narration}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="col_num text-right font-medium">
                          <div className="db_amount_cell">
                            {hasDebit && (
                              <div className="db_amt_val">
                                {row.debit > 0 ? formatCellAmount(row.debit) : ""}
                              </div>
                            )}
                            {hasCredit && <div className="db_dash_amt"></div>}
                            {!hasDebit && !hasCredit && (
                              <div className="db_amt_val">
                                {row.debit > 0 ? formatCellAmount(row.debit) : ""}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="col_num text-right font-medium">
                          <div className="db_amount_cell">
                            {hasDebit && <div className="db_dash_amt"></div>}
                            {hasCredit && (
                              <div className="db_amt_val">
                                {row.credit > 0 ? formatCellAmount(row.credit) : ""}
                              </div>
                            )}
                            {!hasDebit && !hasCredit && (
                              <div className="db_amt_val">
                                {row.credit > 0 ? formatCellAmount(row.credit) : ""}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr className="gst_table_total_row">
                  <td className="col_date"></td>
                  <td className="col_voucher"></td>
                  <td className="col_type"></td>
                  <td className="col_party font-bold text-left">Total</td>
                  <td className="col_num text-right font-bold">
                    {formatTotalAmount(displaySummary.totalDebit)}
                  </td>
                  <td className="col_num text-right font-bold">
                    {formatTotalAmount(displaySummary.totalCredit)}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        {/* 4. Pagination Footer (Matches GST / Tax Register) */}
        <div className="gst_table_footer">
          <div>
            Showing{" "}
            {filteredRecords.length === 0
              ? 0
              : (currentPage - 1) * rowsPerPage + 1}{" "}
            to {Math.min(currentPage * rowsPerPage, filteredRecords.length)} of{" "}
            {filteredRecords.length} records
          </div>

          <div className="d-flex align-items-center gap-1">
            <button
              type="button"
              className="gst_page_btn"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              title="Previous Page"
            >
              <FaChevronLeft style={{ fontSize: "9px" }} />
            </button>
            <span className="gst_page_active_num">{currentPage}</span>
            <button
              type="button"
              className="gst_page_btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              title="Next Page"
            >
              <FaChevronRight style={{ fontSize: "9px" }} />
            </button>
          </div>

          <div className="d-flex align-items-center gap-1">
            <span>Rows per page:</span>
            <select
              className="filter-select"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{ height: 24, padding: "0 4px" }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* 5. Formal Print Area (matching GST / Tax Register) */}
      <div className="db_print_area d-none">
        <div className="print_header_company">
          <h2 className="print_company_name">{companyInfo.name}</h2>
          <p className="print_company_sub">{companyInfo.address}</p>
          {companyInfo.gstin && (
            <p className="print_company_gstin">GSTIN: {companyInfo.gstin}</p>
          )}
        </div>

        <div className="print_report_title">
          <h3>DAY BOOK</h3>
        </div>

        <div className="print_meta_bar">
          <div>
            <strong>Period:</strong> {formatDisplayDate(fromDate)} to{" "}
            {formatDisplayDate(toDate)}
          </div>
          <div>
            <strong>Voucher Type:</strong>{" "}
            {selectedVType === "all" ? "All Types" : selectedVType}
          </div>
          <div>
            <strong>Generated On:</strong> {liveTimestamp}
          </div>
        </div>

        <table className="print_table">
          <thead>
            <tr>
              <th style={{ width: "12%" }}>Date</th>
              <th style={{ width: "13%" }}>Voucher No.</th>
              <th style={{ width: "13%" }}>Type</th>
              <th style={{ width: "38%" }}>Particulars</th>
              <th style={{ width: "12%" }} className="text-right">
                Debit (₹)
              </th>
              <th style={{ width: "12%" }} className="text-right">
                Credit (₹)
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((r) => {
              const isShort = viewMode === "short";
              const firstDebit = r.firstDebit || (r.lines ? r.lines.find((l) => l.type === "Dr")?.ledgerName : null);
              const firstCredit = r.firstCredit || (r.lines ? r.lines.find((l) => l.type === "Cr")?.ledgerName : null);
              const hasDebit = !!firstDebit;
              const hasCredit = !isShort && !!firstCredit;

              return (
                <tr key={r.voucherId}>
                  <td style={{ verticalAlign: "top" }}>{r.date}</td>
                  <td style={{ verticalAlign: "top" }}>{r.voucherNo}</td>
                  <td style={{ verticalAlign: "top" }}>{r.type}</td>
                  <td style={{ verticalAlign: "top" }}>
                    <div className="db_particulars_cell">
                      {hasDebit && (
                        <div className="db_debit_ledger">{firstDebit}</div>
                      )}
                      {hasCredit && (
                        <div className="db_credit_ledger" style={{ paddingLeft: "12px", color: "#1a202c", fontWeight: 600 }}>{firstCredit}</div>
                      )}
                      {!hasDebit && !hasCredit && (
                        <div className="db_debit_ledger">{r.particulars}</div>
                      )}
                      {showNarration && r.narration && (
                        <div
                          className="db_narration_text"
                          style={{
                            fontSize: "10px",
                            color: "#64748b",
                            fontStyle: "italic",
                            marginTop: "2px",
                          }}
                        >
                          Narration: {r.narration}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="text-right" style={{ verticalAlign: "top" }}>
                    <div className="db_amount_cell">
                      {hasDebit && (
                        <div className="db_amt_val">
                          {r.debit > 0 ? formatCellAmount(r.debit) : ""}
                        </div>
                      )}
                      {hasCredit && <div className="db_dash_amt"></div>}
                      {!hasDebit && !hasCredit && (
                        <div className="db_amt_val">
                          {r.debit > 0 ? formatCellAmount(r.debit) : ""}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="text-right" style={{ verticalAlign: "top" }}>
                    <div className="db_amount_cell">
                      {hasDebit && <div className="db_dash_amt"></div>}
                      {hasCredit && (
                        <div className="db_amt_val">
                          {r.credit > 0 ? formatCellAmount(r.credit) : ""}
                        </div>
                      )}
                      {!hasDebit && !hasCredit && (
                        <div className="db_amt_val">
                          {r.credit > 0 ? formatCellAmount(r.credit) : ""}
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="print_total_row">
              <td></td>
              <td></td>
              <td></td>
              <td className="font-bold text-left">Total</td>
              <td className="text-right font-bold">
                {formatTotalAmount(displaySummary.totalDebit)}
              </td>
              <td className="text-right font-bold">
                {formatTotalAmount(displaySummary.totalCredit)}
              </td>
            </tr>
          </tfoot>
        </table>

        <div className="print_footer">
          <div>etour - Design & Develop by Response Infoway</div>
          <div>Page 1 of 1</div>
        </div>
      </div>
    </div>
  );
}
